import '../Authorization.css';
import React, {useState} from 'react';
import Login from "./Login";
import Verification from "./Verification";
import AppHeader from "./AppHeader";
import {
    ACMG_STATUS_APP_PATH,
    MAX_SEND_OTP_ERROR,
    MAX_VERIFY_OTP_ERROR,
    PATIENT_DATA_STORAGE_ITEM_NAME,
    SESSION_ID_STORAGE_ITEM_NAME
} from "../consts/consts";
import {useNavigate} from 'react-router-dom';
import {validateCode, verifyPatientAndSendOtp} from "../api/AuthorizationApi";
import {AcmgStatusObject} from "../utils/AcmgStatusObject";
import {v4 as uuidv4} from "uuid";
import {useLanguage} from "../LanguageContext";
import {getTranslation} from "../consts/translations";

interface AuthorizationProps {
    inputId: string,
    setInputId: (id: string) => void,
    inputPhone: string,
    setInputPhone: (phone: string) => void,
    showVerification: boolean,
    setShowVerification: (show: boolean) => void,
    setAcmgReportStatus: (reportStatus: AcmgStatusObject) => void,
    sessionId: string,
    setSessionId: (sessionId: string) => void,
    onExit: () => void,
    showModal: boolean,
    setShowModal: (showModal: boolean) => void,
}


const Authorization: React.FC<AuthorizationProps> = (props: AuthorizationProps) => {
    const [otp, setOtp] = useState('');
    const [verificationError, setVerificationError] = useState('');
    const [validateButtonLoading, setValidateButtonLoading] = useState(false);
    const [sendButtonLoading, setSendButtonLoading] = useState(false);
    const [sendAgainClicked, setSendAgainClicked] = useState(true);
    const {language} = useLanguage();
    const translation = getTranslation(language);


    const clickExit = () => {
        setVerificationError('');
        props.setShowModal(false);
        props.onExit();
    }

    const navigate = useNavigate();
    const handleSendVerificationCode = async () => {
        setVerificationError('');
        setSendButtonLoading(true);
        try {
            let sessionId = props.sessionId;
            if (!props.sessionId) {
                const newSessionId = uuidv4();
                props.setSessionId(newSessionId);
                sessionId = newSessionId;
            }
            await verifyPatientAndSendOtp(props.inputId, props.inputPhone, sessionId, translation, language);
            sessionStorage.setItem(SESSION_ID_STORAGE_ITEM_NAME, props.sessionId);
            props.setShowVerification(true);
            setSendAgainClicked(true);
        } catch (error: any) {
            if (error.message === MAX_SEND_OTP_ERROR) {
                props.setShowModal(true);
                setVerificationError('');
                return;
            }
            setVerificationError(error.message);
            setSendAgainClicked(false);
        } finally {
            setSendButtonLoading(false);
        }
    };

    const handleValidateCode = async () => {
        setVerificationError('');
        setValidateButtonLoading(true);
        try {
            const data = await validateCode(props.inputId, props.inputPhone, otp, props.sessionId, translation, language);
            props.setAcmgReportStatus(data);
            sessionStorage.setItem(PATIENT_DATA_STORAGE_ITEM_NAME, JSON.stringify(data));
            navigate(ACMG_STATUS_APP_PATH);
        } catch (error: any) {
            if (error.message === MAX_VERIFY_OTP_ERROR) {
                props.setShowModal(true);
                setVerificationError('');
                return;
            }

            setVerificationError(error.message);
        } finally {
            setValidateButtonLoading(false);
            setSendAgainClicked(false);
        }
    };

    return (
        <div className="authorization-view">
            <div className="authorization-background-image"/>
            <AppHeader
                title={translation.login_title}
                subtitle={translation.login_subtitle}
                height="250px"
                showTitle={!props.showModal}
            />
            {props.showModal ?
                (
                    <div className="login-modal">
                        <div className="login-modal-content">
                            <p>{translation.check_input_modal_text}</p>
                            <a href="mailto:acmg.support@psifas.org.il">acmg.support@psifas.org.il</a>
                        </div>
                    </div>
                )
                :
                !props.showVerification ? (
                    <Login
                        inputId={props.inputId}
                        setInputId={props.setInputId}
                        inputPhone={props.inputPhone}
                        setInputPhone={props.setInputPhone}
                        onSendCode={handleSendVerificationCode}
                        loading={sendButtonLoading}
                        setShowVerification={props.setShowVerification}
                        verificationError={verificationError}
                        setVerificationError={setVerificationError}/>
                ) : (
                    <Verification
                        setVerificationCode={setOtp}
                        onValidateCode={handleValidateCode}
                        buttonDisabled={!!otp}
                        loading={validateButtonLoading}
                        verificationError={verificationError}
                        sendVerification={handleSendVerificationCode}
                        inputPhone={props.inputPhone}
                        onExit={clickExit}
                        sendAgainClicked={sendAgainClicked}
                        setSendAgainClicked={setSendAgainClicked}/>
                )}
        </div>
    )
}

export default Authorization;
